import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setGlobalElement, setModalData } from "@/store/globalSlice"
import { allPieces, images } from "@/utils/data"
import classNames from "classnames"
import dummyFlag from "@/images/boards/dummy-flag.png"
import Money from "@/Components/Money"
import React, { useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { mapDesigns } from "@mono/mapDesigns"
import { getPlayerAvatar } from "@/utils/playerAvatar"
import { SignalSlashIcon } from "@heroicons/react/24/solid"
import { gameRoles } from "@/utils/gameRoles"
import { getUserCreds } from "@/utils/utils"
import RoleBadge from "./RoleBadge"
const tempUserImage =
  "https://plus.unsplash.com/premium_photo-1671656349322-41de944d259b?fm=jpg&q=60&w=3000&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MXx8dXNlcnxlbnwwfHwwfHx8MA%3D%3D"

// One rendered flyoff label. Usually 1:1 with a transfer, but transfers sharing
// a labelGroupId (the "get 10% from all players" card) collapse into one item
// whose money is the summed total.
interface FlyoffItem {
  key: string
  money: number
  isStock: boolean
}

// Collapse animations that share a labelGroupId into a single summed item;
// everything else stays 1:1. Order is preserved so the fan-out layout is stable.
const combineFlyoffs = (anims: IMoneyTransferAnimation[]): FlyoffItem[] => {
  const items: FlyoffItem[] = []
  const groupIndexById = new Map<string, number>()
  for (const anim of anims) {
    if (anim.labelGroupId) {
      const existing = groupIndexById.get(anim.labelGroupId)
      if (existing !== undefined) {
        items[existing].money += anim.money
        continue
      }
      groupIndexById.set(anim.labelGroupId, items.length)
      items.push({
        key: anim.labelGroupId,
        money: anim.money,
        isStock: anim.reason === "stock-exchange",
      })
    } else {
      items.push({
        key: anim.id,
        money: anim.money,
        isStock: anim.reason === "stock-exchange",
      })
    }
  }
  return items
}

// A single money flyoff label, rendered once per flyoff item and keyed so it
// mounts fresh and plays its 1.5s fly animation exactly once. A "combined" label
// (e.g. the "get 10% from all players" card) sums several transfers into one
// item. The horizontal `slot`, size `count` and `money` are all captured at
// mount and frozen for the label's lifetime, so it never slides sideways,
// resizes, or has its total shrink as the underlying transfers finish and drop
// out one by one.
const MoneyFlyoff: React.FC<{
  money: number
  isIncrease: boolean
  isStock: boolean
  initialSlot: number
  initialCount: number
  rect: DOMRect
}> = ({ money, isIncrease, isStock, initialSlot, initialCount, rect }) => {
  const slotRef = useRef(initialSlot)
  // Freeze the count at mount so the label keeps a stable size for its whole
  // flight even as other flyoffs for this player come and go. The more transfers
  // are on screen when a label appears, the smaller it renders so stacked labels
  // stay readable.
  const countRef = useRef(initialCount)
  // Freeze the amount so a combined total stays put while its member transfers
  // finish and get removed from the store one at a time.
  const moneyRef = useRef(money)
  const moneySizeClass =
    countRef.current >= 4
      ? "text-5xl"
      : countRef.current === 3
      ? "text-6xl"
      : countRef.current === 2
      ? "text-7xl"
      : "text-8xl"
  const stockLabel = isIncrease ? "Stock Payout" : "Stock Investment"
  const left = rect.left + rect.width / 2 + slotRef.current * 90
  return createPortal(
    <div
      className={classNames(
        "px-10 py-5 fixed hidden -translate-x-1/2 z-[100] flex-col items-center pointer-events-none",
        { "money-fly-up": isIncrease, "money-fly-down": !isIncrease }
      )}
      style={{ left, top: rect.top }}
    >
      {isStock ? (
        <span className="font-poppins font-bold uppercase tracking-wide text-amber-400 text-xl md:text-2xl drop-shadow-[2px_2px_2px_black] whitespace-nowrap">
          {stockLabel}
        </span>
      ) : null}
      <Money
        iconClass="h-0"
        className={classNames(
          moneySizeClass,
          "font-poppins gap-2 font-bold drop-shadow-[2px_2px_2px_black] z-[20]",
          { "text-amber-400": isStock },
          { "text-green-400": isIncrease && !isStock },
          { "text-red-500": !isIncrease && !isStock }
        )}
        sign={isIncrease ? "+" : "-"}
        money={moneyRef.current}
      ></Money>
    </div>,
    document.body
  )
}

export interface PlayerListItemInfoProps {
  player: IPlayer
  user: IUser
  distinguishActivePlayer?: boolean
  forTradeScreen?: boolean
  forAuctionScreen?: boolean
}

const PlayerListItemInfo2: React.FC<PlayerListItemInfoProps> = ({
  player,
  user,
  distinguishActivePlayer = true,
  forTradeScreen = false,
  forAuctionScreen = false,
}) => {
  const piece = allPieces.find((item) => item.id === player.pieceId)
  const wrapperRef = useRef(null)
  const dispatch = useAppDispatch()
  const mapId = useAppSelector((state) => state.global.myGame?.settings.mapId)
  const elements = useAppSelector((state) => state.global.elements)
  const map = mapDesigns.find((map) => map.id === mapId)
  const moneyAnimations = useAppSelector((state) => state.global.moneyAnimations)
  const situation = useAppSelector((state) => state.global.myGame?.situation)
  useEffect(() => {
    if (!wrapperRef.current) return
    if (elements.playerListItems.find((elm) => elm.order === player.order)) return
    dispatch(
      setGlobalElement({
        playerListItems: [...elements.playerListItems, { order: player.order, element: wrapperRef.current }],
      })
    )
  }, [elements.playerListItems, dispatch, player.order])
  // A player can be part of several transfers at once (e.g. collecting rent from
  // one player while paying tax to the bank). Render a flyoff for *every*
  // matching animation rather than a single one, so concurrent gains/losses
  // stack instead of overwriting each other. Money arriving (to === order) flies
  // up in green; money leaving (from === order) flies down in red.
  const showFlyoffs = !forTradeScreen && !forAuctionScreen
  const increaseItems = showFlyoffs
    ? combineFlyoffs(
        moneyAnimations.filter((anim: IMoneyTransferAnimation) => anim.to === player.order)
      )
    : []
  const decreaseItems = showFlyoffs
    ? combineFlyoffs(
        moneyAnimations.filter((anim: IMoneyTransferAnimation) => anim.from === player.order)
      )
    : []

  // The flyoff is rendered through a portal to document.body so it escapes the
  // left sidebar's stacking context and paints on top of the flying bills,
  // without touching the bills' z-index or pushing them behind the player card.
  const flyoffRect =
    (increaseItems.length || decreaseItems.length) && wrapperRef.current
      ? (wrapperRef.current as HTMLDivElement).getBoundingClientRect()
      : null


  const handleCardClick = () => {
    // if (getUserCreds().email !== player.id) return
    dispatch(
      setModalData({
        playerCards: { visible: true, playerOrder: player.order },
      })
    )
  }
  const myAvatar = getPlayerAvatar(player, user)
  const playerRole = gameRoles.find((r) => r.id === player.role)
  // private-buyer and financer's numbers depend on the map (see
  // getUpdatedGameAfterRolePicking in gameHelpers.ts) — mapId "0" is the
  // Standard board, everything else (e.g. "1", Mini map) gets the smaller
  // values, so the tooltip's effect text needs to match whichever board is
  // actually in play instead of the generic copy in gameRoles.ts.
  const isStandardMap = mapId === "0"
  const roleEffectOverride =
    player.role === "private-buyer"
      ? `Can buy ${isStandardMap ? 4 : 3} properties on landing without auction (${
          player.canBuyPropertiesWithoutAuction
        } left)`
      : player.role === "financer"
      ? `Get +${isStandardMap ? 500 : 250} in start and +100(total 300) on round completion`
      : undefined

  return (
    <div
      ref={wrapperRef}
      style={{
        boxShadow: situation?.turn === player.id ? "inset 0px 0px 40px 0px #ffe10092" : "none",
      }}
      className={classNames(
        "flex relative justify-between items-center player-list-item-info  flex-col  py-1 md:min-h-[4rem] md:py-0 px-2 md:px-4 shadow-md rounded-t-lg",
        {
          "grayscale bankrupted": player.isBankrupt,
          // "shadow-[inset_0px_0px_51px_#fecc5c] active":
          //   state.myGame?.situation?.turn === player.id &&
          //   !forTradeScreen &&
          //   !forAuctionScreen &&
          //   !player.isBankrupt,
          "bg-transparent": forTradeScreen || forAuctionScreen,
          "!bg-transparent": !forTradeScreen && !forAuctionScreen,
        }
      )}
      data-testid={`game.player-tab-${player.order}`}
    >
      {flyoffRect
        ? [
            ...increaseItems.map((item, i) => (
              <MoneyFlyoff
                key={item.key}
                money={item.money}
                isIncrease
                isStock={item.isStock}
                initialSlot={i - (increaseItems.length - 1) / 2}
                initialCount={increaseItems.length + decreaseItems.length}
                rect={flyoffRect}
              />
            )),
            ...decreaseItems.map((item, i) => (
              <MoneyFlyoff
                key={item.key}
                money={item.money}
                isIncrease={false}
                isStock={item.isStock}
                initialSlot={i - (decreaseItems.length - 1) / 2}
                initialCount={increaseItems.length + decreaseItems.length}
                rect={flyoffRect}
              />
            )),
          ]
        : null}
      <div className="flex gap-4 items-center min-w-0 max-w-full pr-1">
        {/* Once a bot has the seat there is somebody playing it again, so the
            no-signal icon would be misleading — the bot badge below says what is
            actually going on. */}
        {player.disconnected && !player.bot && (
          <SignalSlashIcon color="white" className="h-6 w-6" />
        )}
        <img className="sm:h-10 sm:w-10 h-7 w-7" src={piece?.imgSrc}></img>
        <div className="flex gap-2 items-center min-w-0">
          <img
            className="h-9 w-9 player-image rounded-full object-cover md:block hidden"
            src={user.profileImage ? encodeURI(user.profileImage) : myAvatar ? myAvatar : tempUserImage}
          ></img>
          <div className="flex flex-col gap-3 min-w-0">
            <div className="flex gap-2 items-center min-w-0">
              <div className="flex flex-col gap-2 min-w-0">
                <div className="font-poppins text-white text-base sm:text-2xl sm:font-semibold min-w-0">
                  <div className="flex items-center gap-1.5">
                    {/* Capped in rem so it shrinks along with the html font size
                        on narrower screens, keeping the money on the right of
                        the card from being pushed out by a long name. */}
                    <span className="max-w-[9rem] truncate" title={user.username}>
                      {user.username}
                    </span>
                    {player.bot && (
                      <div
                        className="flex items-center gap-1 px-1.5 h-[13px] md:h-[18px] rounded-md bg-sky-500/20 text-sky-300 text-[7px] md:text-[10px] font-bold font-poppins uppercase tracking-wide leading-none shrink-0"
                        title="This player left — a bot is playing their seat"
                        data-testid={`game.player-bot-badge-${player.order}`}
                      >
                        <i className="fa-solid fa-robot text-[6px] md:text-[9px]"></i>
                        Bot
                      </div>
                    )}
                  </div>

                  {player.role !== "none" && playerRole && (
                    // Private buyer's remaining no-auction buys ride inside the
                    // role pill instead of sitting next to it as a second badge:
                    // one child can't wrap, so this row is always exactly one
                    // line and the card stays the same height as the others.
                    <div className="flex items-center gap-1.5 flex-nowrap min-w-0">
                      <RoleBadge
                        role={playerRole}
                        playerOrder={player.order}
                        effect={roleEffectOverride}
                        count={
                          player.role === "private-buyer" &&
                          player.canBuyPropertiesWithoutAuction > 0
                            ? {
                                icon: "fa-solid fa-cart-shopping",
                                value: player.canBuyPropertiesWithoutAuction,
                              }
                            : undefined
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
              {/* {player.role !== "none" && (
                <div
                  className="px-2 py-0.5 rounded-md text-xs font-medium font-poppins h-fit md:flex hidden"
                  style={{

                    backgroundColor:
                      gameRoles.find((r) => r.id === player.role)?.color + "40",
                    color: gameRoles.find((r) => r.id === player.role)?.color,
                  }}
                >
                  {gameRoles.find((r) => r.id === player.role)?.title}
                </div>
              )} */}

              {/* <img src={dummyFlag}></img> */}
            </div>
            {/* <div className="text-[#B5B4B4] text-xs font-poppins"> */}
            {/*   Skale Enjoyoor */}
            {/* </div> */}
          </div>
        </div>
      </div>
      {/* <div
            className="text-white text-2xl font-semibold font-poppins"
            data-testid={`game.player-money-${player.order}`}
         > */}

      {player.isBankrupt ? (
        <div className="text-xl text-white font-poppins font-bold">Bankrupt</div>
      ) : (
        <div className="flex gap-2 items-center shrink-0">
          <img
            className={classNames("h-5 md:h-8 cursor-pointer", {
              "opacity-0": getUserCreds().email !== player.id,
            })}
            src={images.icons.cards}
            onClick={handleCardClick}
            data-testid={`game.player-cards-btn-${player.order}`}
          ></img>
          <Money
            money={player.money}
            testId={forTradeScreen ? `auction.money-${player.order}` : `game.player-money-${player.order}`}
            iconClass="h-1/2"
            className={classNames("text-white text-4xl font-semibold font-poppins gap-1", {
              "text-red-white": player.money < 0,
            })}
          ></Money>
        </div>
      )}
    </div>
  )
}

export default React.memo(PlayerListItemInfo2)
